import React, { useCallback, useState } from 'react';
import VoxelAlert from '../../../components/voxel/Alert/VoxelAlert';
import VoxelPanel from '../../../components/voxel/Surfaces/VoxelPanel';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import VoxelAdvancedTextSearch from '../../../components/voxel/Search/VoxelAdvancedTextSearch';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { PagingInputGq } from '../../../graphql/users/models/PagingInputGq';
import { usersApiService } from '../../../graphql/users/usersApiService';
import TeamAccessTable from './TeamAccessTable';
import AddTeamAccessDialog from './AddTeamAccessDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';

export interface TeamAccessPageProps {
  project: ProjectGq;
  reload: () => Promise<any>;
  user: CurrentUserGq;
}

export const TeamAccessPage: React.FC<TeamAccessPageProps> = ({ user, project, reload }) => {
  // Get invited teams to this project
  const [teamPagingInput, setTeamPagingInput] = useState<PagingInputGq>({
    page: 0,
    amountPerPage: 8,
  });

  const [searchTerm, setSearchTerm] = useState<string>('');
  const {
    teams,
    query: teamsQuery,
    pagingResult: teamPagingResult,
  } = usersApiService.queries.useGetProjectAccessTeams({
    pagingInput: teamPagingInput,
    projectId: project.id,
    excludeTeamIds: [project.createdBy.team.id],
    searchTerm: searchTerm,
  });

  const handleReload = useCallback(
    () => Promise.all<unknown>([teamsQuery.refetch(), reload()]),
    [teamsQuery.refetch, reload]
  );

  const [showAddTeam, setShowAddTeam] = useState(false);

  // Invited users should not be able to see this view
  const userInSameTeam = user.team.id === project?.createdBy?.team?.id;
  const userCreatedProject = user.id === project?.createdById;
  const userHasAccess = userInSameTeam || userCreatedProject;
  if (!userHasAccess)
    return (
      <VoxelAlert>
        Invited team access is only available to the team that created the project
      </VoxelAlert>
    );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-row justify-between items-start">
        <div>
          <VoxelText variant="title2" paddingBot>
            Team access
          </VoxelText>
          <VoxelText variant="bread-medium">
            Share a specific project with a connected team. This is useful for agencies that have a
            "reciving" team connection with clients.
          </VoxelText>
        </div>
        <VoxelButton
          disabled={!userHasAccess}
          endIcon={<FontAwesomeIcon icon={faPlus} />}
          title="Add connected team access"
          onClick={() => setShowAddTeam(true)}
          color={!!showAddTeam ? 'primary' : 'gray'}
        />
      </div>

      {/* Invited teams */}
      <VoxelPanel>
        <div className="p-3 pb-0">
          <div className="w-full flex flex-row gap-8 justify-between">
            <div>
              <VoxelAdvancedTextSearch
                size="sm"
                placeholder="Search team"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                deleteValue={() => setSearchTerm('')}
              />
            </div>
          </div>
        </div>
        <TeamAccessTable
          paging={teamPagingInput}
          pagingResult={teamPagingResult}
          setPaging={setTeamPagingInput}
          user={user}
          teamProjectAccess={teams}
          reload={handleReload}
          project={project}
        />
      </VoxelPanel>
      {showAddTeam && (
        <AddTeamAccessDialog
          open={showAddTeam}
          setOpen={setShowAddTeam}
          reload={reload}
          project={project}
          user={user}
        />
      )}
    </div>
  );
};

export default TeamAccessPage;
